import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  FileUploaderButton,
  NumberInput,
  Select,
  SelectItem,
  TextArea,
  TextInput,
} from "@carbon/react";
import { Edit } from "@carbon/icons-react";
import { FileReader } from "../../../components";
import DetailSummaryList from "./DetailSummaryList";
import { queryKeys } from "../../../hooks/queryKeys";
import { useFileInputUploader } from "../../../hooks";
import {
  useUpdatePurchaseRequestV2,
  useUpdateRequestItemV2,
} from "../../../hooks/usePurchaseRequestsV2";
import {
  CURRENCY_OPTIONS,
  FileType,
  PurchaseRequestBudgetType,
  PurchaseRequestFilePayload,
  PurchaseRequestItemV2,
  PurchaseRequestV2,
} from "../../../types";
import { fileText } from "../../../utils/utils";
import {
  computeBudgetSummary,
  hasGeneralChanges,
  normalizeFiles,
} from "./detailHelpers";

export interface DetailGeneralTabProps {
  request: PurchaseRequestV2;
  activeItems: PurchaseRequestItemV2[];
  canEditRequest: boolean;
  isBusy: boolean;
  onGlobalCurrencyChange: (currency: string) => void;
}

/**
 * The "Details" tab (Task 5) — the pre-Carbon general-info form (name / comments / attachments) plus
 * the request summary panel (company, requester, created date, needed dates, document class, budget)
 * rebuilt on Carbon form controls and `StructuredList`. The editable "global budget" (shown only
 * when every item shares one identical budget) and the save-metadata flow are preserved verbatim.
 */
const DetailGeneralTab = ({
  request,
  activeItems,
  canEditRequest,
  isBusy,
  onGlobalCurrencyChange,
}: DetailGeneralTabProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [comments, setComments] = useState("");
  const [originChannel, setOriginChannel] = useState("");
  const [originMetadata, setOriginMetadata] = useState("");
  const [draft, setDraft] = useState(true);
  const [files, setFiles] = useState<PurchaseRequestFilePayload[]>([]);
  const [pendingFileName, setPendingFileName] = useState("");
  const [globalBudgetValue, setGlobalBudgetValue] = useState("");
  const [globalCurrency, setGlobalCurrency] = useState("");
  const [globalBudgetType, setGlobalBudgetType] =
    useState<PurchaseRequestBudgetType>(PurchaseRequestBudgetType.TOTAL);
  const [savedGlobalBudgetValue, setSavedGlobalBudgetValue] = useState("");
  const [savedGlobalCurrency, setSavedGlobalCurrency] = useState("");
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    setName(request.name || "");
    setComments(request.comments || "");
    setOriginChannel(request.originChannel || "");
    setOriginMetadata(request.originMetadata || "");
    setDraft(request.draft);
    setFiles(
      request.files?.map(({ id, fileURL, name: fileName, type }) => ({
        id,
        fileURL,
        name: fileName,
        type,
      })) || []
    );
    const items = request.items.filter((i) => !i.deleted);
    const withBudget = items.filter((i) => i.budgetValue != null);
    if (withBudget.length > 0) {
      const initialBudgetValue = String(withBudget[0].budgetValue ?? "");
      const initialCurrency = withBudget[0].budgetCurrency || "";
      setGlobalBudgetValue(initialBudgetValue);
      setGlobalCurrency(initialCurrency);
      setSavedGlobalBudgetValue(initialBudgetValue);
      setSavedGlobalCurrency(initialCurrency);
      setGlobalBudgetType(
        withBudget[0].budgetType || PurchaseRequestBudgetType.TOTAL
      );
      onGlobalCurrencyChange(initialCurrency);
    }
  }, [request, onGlobalCurrencyChange]);

  const { mutate: uploadAttachment, isPending: isUploadingAttachment } =
    useFileInputUploader((fileURL: string) => {
      if (!fileURL) return;
      const tempId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setFiles((prev) => [
        ...prev,
        {
          clientId: tempId,
          fileURL,
          name: pendingFileName || fileText(FileType.ATTACHMENT),
          type: FileType.ATTACHMENT,
        },
      ]);
      setPendingFileName("");
    });

  const { mutate: updateRequest, isPending: isUpdatingRequest } =
    useUpdatePurchaseRequestV2(() => {});
  const { mutateAsync: updateItemAsync } = useUpdateRequestItemV2(() => {});

  const busy = isBusy || isUpdatingRequest || isUploadingAttachment || isSending;

  const { itemsWithBudget, allBudgetsSame } = computeBudgetSummary(activeItems);

  const generalChanged = hasGeneralChanges(
    { name, comments, originChannel, originMetadata, draft, files },
    request
  );
  const budgetChanged =
    globalBudgetValue !== savedGlobalBudgetValue ||
    globalCurrency !== savedGlobalCurrency;

  const handleAttachmentUpload = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!canEditRequest) return;
    const file = event.target.files?.[0];
    if (!file) return;
    setPendingFileName(file.name);
    uploadAttachment(file);
    event.target.value = "";
  };

  const handleRemoveFile = (fileKey: string) => {
    if (!canEditRequest) return;
    setFiles((prev) =>
      prev.filter(
        (file) =>
          file.clientId !== fileKey &&
          file.id !== fileKey &&
          file.fileURL !== fileKey
      )
    );
  };

  const handleSaveMetadata = () => {
    if (!canEditRequest) return;
    updateRequest({
      id: request.id,
      body: {
        ...request,
        name: name || null,
        comments: comments || null,
        originChannel: originChannel || null,
        originMetadata: originMetadata || null,
        files: normalizeFiles(files),
        draft,
      },
    });
  };

  const handleSaveGlobalBudget = async () => {
    if (!canEditRequest) return;
    const items = request.items.filter((i) => !i.deleted);
    if (!items.length) return;
    setIsSending(true);
    try {
      const budgetVal =
        globalBudgetValue !== "" ? Number(globalBudgetValue) : null;
      await Promise.all(
        items.map((item) =>
          updateItemAsync({
            itemId: item.id,
            body: {
              ...item,
              budgetValue: budgetVal,
              currency: globalCurrency || null,
              budgetCurrency: globalCurrency || null,
              budgetType: globalBudgetType,
            },
          })
        )
      );
      setSavedGlobalBudgetValue(globalBudgetValue);
      setSavedGlobalCurrency(globalCurrency);
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_V2, request.id],
      });
    } finally {
      setIsSending(false);
    }
  };

  const firstItem = activeItems[0];

  return (
    <div className="pr-detail__general">
      <section className="pr-detail__general-form cds-form-surface">
        <TextInput
          id="pr-detail-name"
          labelText={t("purchaseRequests.fields.name")}
          value={name}
          onChange={(event) => setName(event.target.value)}
          readOnly={!canEditRequest}
        />
        <TextArea
          id="pr-detail-comments"
          labelText={t("purchaseRequests.fields.comments")}
          rows={3}
          value={comments}
          onChange={(event) => setComments(event.target.value)}
          readOnly={!canEditRequest}
        />

        <div className="pr-detail__files">
          <div className="pr-detail__files-header">
            <h3 className="pr-detail__section-title">
              {t("purchaseRequests.files")}
            </h3>
            {canEditRequest ? (
              <FileUploaderButton
                buttonKind="tertiary"
                size="sm"
                labelText={t("cta.add")}
                disableLabelChanges
                onChange={handleAttachmentUpload}
                disabled={busy}
              />
            ) : null}
          </div>
          {files.length ? (
            <div className="pr-detail__files-list">
              {files.map((file) => (
                <div className="pr-detail__file" key={file.id || file.fileURL}>
                  <div>
                    <p className="pr-detail__file-type">
                      {fileText((file.type as FileType) || FileType.ATTACHMENT)}
                    </p>
                    <p className="pr-detail__file-name">
                      {file.name ||
                        fileText(
                          (file.type as FileType) || FileType.ATTACHMENT
                        )}
                    </p>
                  </div>
                  <div className="pr-detail__file-actions">
                    <FileReader fileKey={file.fileURL} />
                    {canEditRequest ? (
                      <Button
                        kind="ghost"
                        size="sm"
                        onClick={() =>
                          handleRemoveFile(file.id || file.fileURL)
                        }
                        disabled={busy}
                      >
                        {t("cta.delete")}
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="pr-detail__muted">
              {t("purchaseRequests.noFiles", {
                defaultValue: "No attachments added",
              })}
            </p>
          )}
        </div>

        {canEditRequest ? (
          <div className="pr-detail__form-actions">
            <Button onClick={handleSaveMetadata} disabled={busy || !generalChanged}>
              {t("cta.save")}
            </Button>
          </div>
        ) : null}
      </section>

      <section className="pr-detail__summary cds-form-surface">
        <h3 className="pr-detail__section-title">
          {t("purchaseRequests.detailTitle")}
        </h3>
        <DetailSummaryList
          request={request}
          firstItem={firstItem}
          readOnlyBudget={
            allBudgetsSame && !canEditRequest
              ? {
                  value: itemsWithBudget[0]?.budgetValue ?? undefined,
                  currency: itemsWithBudget[0]?.currency || undefined,
                }
              : null
          }
        />

        {allBudgetsSame && canEditRequest ? (
          <div className="pr-detail__budget-editor">
            <Select
              id="pr-detail-global-currency"
              labelText={t("purchaseRequests.budgetType.currency", {
                defaultValue: "Currency",
              })}
              value={globalCurrency}
              onChange={(event) => {
                setGlobalCurrency(event.target.value);
                onGlobalCurrencyChange(event.target.value);
              }}
            >
              <SelectItem value="" text="-" />
              {CURRENCY_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  text={option.label}
                />
              ))}
            </Select>
            <NumberInput
              id="pr-detail-global-budget"
              label={t("purchaseRequests.items.columns.budgetValue")}
              value={globalBudgetValue}
              onChange={(_event, { value }) =>
                setGlobalBudgetValue(value === "" || value == null ? "" : String(value))
              }
              hideSteppers
            />
            <Button
              kind="ghost"
              hasIconOnly
              renderIcon={Edit}
              iconDescription={t("cta.save")}
              onClick={handleSaveGlobalBudget}
              disabled={busy || !budgetChanged}
            />
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default DetailGeneralTab;
